// Merkan · PDF seitenweise als Bild darstellen (pdf.js). Für Smartphone und Tablet, wo eingebettete PDFs im iframe
// nur die erste Seite zeigen, nicht scrollen oder gar nicht erscheinen. Am PC nutzt PdfVorschau die Ansicht des Browsers.
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import { mobilesGeraet } from '../lib/teilen';
import { t } from '../lib/sprache';
import './pdf-seiten.css';

let pdfjsLaden: Promise<any> | null = null;
function pdfjs(): Promise<any> {
  pdfjsLaden ??= (async () => {
    // Legacy-Build: läuft auch auf älteren iOS-Versionen
    const [lib, worker] = await Promise.all([
      import('pdfjs-dist/legacy/build/pdf.mjs'),
      import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url'),
    ]);
    lib.GlobalWorkerOptions.workerSrc = (worker as { default: string }).default;
    return lib;
  })();
  pdfjsLaden.catch(() => { pdfjsLaden = null; });
  return pdfjsLaden;
}

async function bytesVon(q: Blob | string): Promise<Uint8Array> {
  if (typeof q !== 'string') return new Uint8Array(await q.arrayBuffer());
  const r = await fetch(q);
  if (!r.ok) throw new Error(t('Die Datei konnte nicht geladen werden.'));
  return new Uint8Array(await r.arrayBuffer());
}

/** Alle Seiten untereinander, auf die Breite des Behälters eingepasst; zoom vergrößert über die Breite hinaus */
export function PdfSeiten({ quelle, zoom = 1, titel }: { quelle: Blob | string; zoom?: number; titel?: string }) {
  const huelle = useRef<HTMLDivElement>(null);
  const [dok, setDok] = useState<any>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [breite, setBreite] = useState(0);

  useEffect(() => {
    let aktiv = true;
    let geladen: any = null;
    setDok(null);
    setFehler(null);
    void (async () => {
      try {
        const [lib, daten] = await Promise.all([pdfjs(), bytesVon(quelle)]);
        geladen = await lib.getDocument({ data: daten, isEvalSupported: false }).promise;
        if (aktiv) setDok(geladen); else void geladen.destroy();
      } catch (e) {
        if (aktiv) setFehler((e as Error)?.message || t('Das PDF konnte nicht angezeigt werden.'));
      }
    })();
    return () => { aktiv = false; if (geladen) void geladen.destroy(); };
  }, [quelle]);

  useEffect(() => {
    const el = huelle.current;
    if (!el) return;
    let takt = 0;
    const messen = () => { window.clearTimeout(takt); takt = window.setTimeout(() => setBreite(el.clientWidth), 80); };
    setBreite(el.clientWidth);
    const ro = new ResizeObserver(messen);
    ro.observe(el);
    return () => { ro.disconnect(); window.clearTimeout(takt); };
  }, []);

  const seiten = dok ? Array.from({ length: dok.numPages as number }, (_, i) => i + 1) : [];
  return (
    <div ref={huelle} className="pdfs-mass">
      <div className="pdfs" aria-label={titel ?? t('PDF')}>
        {fehler && <div className="pdfs-meldung">{fehler}</div>}
        {!fehler && !dok && <div className="pdfs-meldung">{t('PDF wird geladen …')}</div>}
        {dok && breite > 0 && seiten.map((n) => <Seite key={n} dok={dok} nummer={n} breite={Math.round(Math.max(200, breite - 2) * zoom)} />)}
      </div>
    </div>
  );
}

function Seite({ dok, nummer, breite }: { dok: any; nummer: number; breite: number }) {
  const leinwand = useRef<HTMLCanvasElement>(null);
  const [hoehe, setHoehe] = useState(Math.round(breite * 1.414));

  useEffect(() => {
    let aktiv = true;
    let auftrag: any = null;
    void (async () => {
      const seite = await dok.getPage(nummer);
      if (!aktiv) return;
      const basis = seite.getViewport({ scale: 1 });
      const massstab = breite / basis.width;
      // Schärfe: Pixeldichte des Geräts, begrenzt wegen Speicher auf iOS (Canvas-Obergrenze)
      const flaeche = breite * breite * (basis.height / basis.width);
      const dichte = Math.max(1, Math.min(window.devicePixelRatio || 1, 2.5, Math.sqrt(14_000_000 / flaeche)));
      const vp = seite.getViewport({ scale: massstab * dichte });
      const c = leinwand.current;
      if (!c) return;
      c.width = Math.floor(vp.width);
      c.height = Math.floor(vp.height);
      setHoehe(Math.round(vp.height / dichte));
      auftrag = seite.render({ canvasContext: c.getContext('2d'), viewport: vp });
      try { await auftrag.promise; } catch { /* abgebrochen */ }
    })();
    return () => { aktiv = false; auftrag?.cancel?.(); };
  }, [dok, nummer, breite]);

  return <canvas ref={leinwand} className="pdfs-seite" style={{ width: breite, height: hoehe }} aria-label={t('Seite {n}', { n: nummer })} />;
}

/** Eingebettete Vorschau: am PC die PDF-Ansicht des Browsers, auf Smartphone und Tablet pdf.js */
export function PdfVorschau({ url, titel, className }: { url: string; titel: string; className?: string }) {
  if (!mobilesGeraet()) return <iframe src={url} title={titel} className={className} />;
  return <div className={['pdfs-vorschau', className].filter(Boolean).join(' ')}><PdfSeiten quelle={url} titel={titel} /></div>;
}
